import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { useForm } from 'react-hook-form'
import {
    Grid,
    Row,
    Col,
    SectionTitle,
    ContainerWithPadding,
    Photo,
    IdFormField,
    TextFormField,
    TextAreaField,
    nameof,
    MediaSelector,
    Buttons,
} from 'traveldesk-ui'
import { ApplicationState } from '../store'
import { actionCreators as toursCategoriesActions } from '../store/ToursCategories'
import { actionCreators as languagesActions } from '../store/Languages'
import { TourCategoryModel } from '../models/TourCategoryModel'
import { urls } from '../urls'
import { Link } from 'react-router-dom'
import TranslationsTable from '../components/TranslationsTable'
import { MetaDataModelTourCategory } from '../models/MetaDataModel'

interface Props extends RouteComponentProps<{ tourCategoryId: string }> { }
const TourCategoryFormPage = (props: Props) => {
    const dispatch = useDispatch()
    const Localization = useSelector((state: ApplicationState) => state.localization.locale)
    const languages = useSelector((state: ApplicationState) => state.languages.data)
    const toursCategories = useSelector((state: ApplicationState) => state.toursCategories.manageList)
    const { register, handleSubmit, formState: { errors }, control, getValues, setValue, watch, reset, setError } = useForm()
    React.useEffect(() => {
        if (languages.length == 0) dispatch(languagesActions.requestData())
    }, [])
    const tc = useSelector((state: ApplicationState) => state.toursCategories.edit)
    React.useEffect(() => {
        return () => {
            dispatch(toursCategoriesActions.edit(undefined))
        }
    }, [])
    React.useEffect(() => {
        if (languages.length == 0) {
            dispatch(languagesActions.requestData())
        }
        if (toursCategories.length == 0) {
            dispatch(toursCategoriesActions.requestManageList())
        }
    }, [])
    React.useEffect(() => {
        const id = parseInt(props.match.params.tourCategoryId)
        if (toursCategories.length > 0) {
            const c = toursCategories.find((x) => x.id == id) || new TourCategoryModel()
            if (c.id != id) {
                c.id = -1
            }
            dispatch(toursCategoriesActions.edit(c))
        }
    }, [toursCategories, props.match.params.tourCategoryId])

    React.useEffect(() => {
        setPhotos(tc?.photos ?? [])
        setHeroImageId(tc?.heroImageId)
        reset(tc)
    }, [tc?.id || -1])
    const [heroImageId, setHeroImageId] = React.useState(tc?.heroImageId)
    const onHeroImageChanged = (photo: Photo) => {
        setHeroImageId(photo.id)
    }
    const [photos, setPhotos] = React.useState(tc?.photos ?? [])
    const onSelectPhotos = (newPhotos: Photo[]) => {
        setPhotos(photos.concat(newPhotos))
    }
    const onSubmit = (data: any) => {
        const model = Object.assign(TourCategoryModel.Create(data), {
            metaData: tc?.metaData ?? [],
            heroImageId,
            photos,
        })
        dispatch(toursCategoriesActions.saveData(model))
    }
    const translationsData = React.useMemo(() => {
        const l = languages.filter((x) => !x.isDeleted)
        if (tc) {
            const res = l.map((x) =>
                Object.assign(
                    tc.metaData.find((m) => m.languageId == x.id) ?? MetaDataModelTourCategory.FromLanguage(x, tc.id),
                    {
                        languageName: x.name,
                    },
                ),
            )
            return res
        }
        return []
    }, [languages, tc])
    return (
        <ContainerWithPadding>
            {tc ? (
                <Grid>
                    {' '}
                    <SectionTitle
                        title={
                            tc.id > 0
                                ? `${Localization.getString('Edit Tour Category')} ${tc.name}`
                                : `${Localization.getString('Add Tour Category')}`
                        }
                    />
                    <Link className="a-back-to-list" to={urls.toursCategories}>
                        {Localization.getString(`Back to Tours Categories`)}
                    </Link>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Row>
                            <Col type="50">
                                <IdFormField control={control} />
                                <TextFormField
                                    label={Localization.getString('Tour Category Name')}
                                    placeholder={Localization.getString('Name of tour category')}
                                    isRequired
                                    error={errors.name ? errors.name.message : ''}
                                    name={nameof<TourCategoryModel>('name')}
                                    register={register}
                                    options={{
                                        required: Localization.getString('Tour category name is required'),
                                    }}
                                />
                                <TextFormField
                                    label={Localization.getString('Slug')}
                                    placeholder={Localization.getString('Url of the tour category')}
                                    isRequired
                                    error={errors.url ? errors.url.message : ''}
                                    name={nameof<TourCategoryModel>('slug')}
                                    register={register}
                                    options={{
                                        required: Localization.getString('Tour category slug is required'),
                                    }}
                                />
                                <TextFormField
                                    label={`${Localization.getString('Page Title')} (${Localization.getString('English')})`}
                                    name={nameof<TourCategoryModel>('pageTitle')}
                                    register={register}
                                />
                            </Col>
                            <Col type="50">
                                <TextAreaField
                                    label={`${Localization.getString('Short Description')} (${Localization.getString('English')})`}
                                    name={nameof<TourCategoryModel>('shortDescription')}
                                    register={register}
                                />
                                <TextAreaField
                                    label={`${Localization.getString('Description')} (${Localization.getString('English')})`}
                                    name={nameof<TourCategoryModel>('description')}
                                    register={register}
                                />
                                <MediaSelector
                                    heroImageId={heroImageId}
                                    onHeroImageChanged={onHeroImageChanged}
                                    selected={photos}
                                    onSelectPhotos={onSelectPhotos}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col type="100">
                                <TranslationsTable
                                    data={translationsData}
                                    entityId={tc.id}
                                    apiPath={`/api/tourscategories/${tc.id}`}
                                    nameLabel={Localization.getString('Tour Category Name')}
                                    localize={(key) => Localization.getString(key)}
                                    onTranslationChanged={(translation) =>
                                        dispatch(
                                            toursCategoriesActions.saveTranslation(
                                                tc.id,
                                                translation as MetaDataModelTourCategory,
                                            ),
                                        )
                                    }
                                />
                            </Col>
                        </Row>
                        <Buttons.StickySaveButton>{Localization.getString('Save')}</Buttons.StickySaveButton>
                    </form>
                </Grid>
            ) : null}
        </ContainerWithPadding>
    )
}
export default TourCategoryFormPage
